import { push } from 'connected-react-router';
import { withFormik } from 'formik';
import extend from 'lodash/extend';
import { connect } from 'react-redux';
import { compose, setDisplayName } from 'recompact';

import type { UnifiedEvent } from 'src/app/analytics/analyticsEvents';
import { ANALYTICS_EVENTS, UNIFIED } from 'src/app/analytics/analyticsEvents';
import { errorBoundary, withStandardPageAndLoading, withStripeElements } from 'src/app/hocs';
import { extractValidationErrors } from 'src/app/popsicle/utils';
import { accountPage } from 'src/app/router/paths';
import { openModal, segmentAnalyticsEvent, segmentTrack } from 'src/app/state/actions';
import { postPaymentCard } from 'src/app/state/actions/account';
import { getCurrentAccountId } from 'src/app/state/selectors';
import type { Dispatch, RootState } from 'src/app/state/store';

import Renderer from '.';

function makeMapStateToProps() {
  return (state: RootState) => {
    const accountId = getCurrentAccountId(state);
    return {
      accountId,
    };
  };
}

const mapDispatchToProps = (dispatch: Dispatch) => ({
  dispatchSaveCard: async stripe => {
    await dispatch(postPaymentCard(stripe));
  },
  dispatchPushAccountPage: () => dispatch(push(accountPage.toPath())),
  dispatchAddCreditCardFailure: attributes =>
    dispatch(segmentAnalyticsEvent(ANALYTICS_EVENTS.addCreditCardFailure, { ...attributes })),
  dispatchSegmentTrack: (event: UnifiedEvent, payload) => dispatch(segmentTrack(event, payload)),
  dispatchOpenModal: ({ modalType, handleConfirmClick, confirmMessage }) =>
    dispatch(openModal({ modalType, handleConfirmClick, confirmMessage })),
});

const enhanceWithFormik = withFormik({
  displayName: 'CreditCardForm',
  handleSubmit: async (values, { props, setErrors, setSubmitting }) => {
    const {
      stripe,
      dispatchSaveCard,
      dispatchAddCreditCardFailure,
      dispatchPushAccountPage,
      dispatchSegmentTrack,
      dispatchOpenModal,
    } = props;

    const dispatchAnalytics = (errorCode: any, errorSummary: any) => {
      dispatchAddCreditCardFailure({
        error_code: errorCode,
        error_summary: errorSummary,
      });
    };

    setSubmitting(true);

    try {
      await dispatchSaveCard(stripe);
      dispatchSegmentTrack(UNIFIED.addACreditCard, { source: 'account' });
      dispatchOpenModal({
        modalType: 'ok',
        confirmMessage: 'Great! Your credit card has been uploaded.',
      });
      dispatchPushAccountPage();
    } catch (error) {
      const bffError = error?.error;
      const errorCode = bffError?.errorCode;
      const errorSummary = bffError?.flavorText;

      const errors = extractValidationErrors(error);

      dispatchAnalytics(errorCode ? errorCode : '400', errorSummary ? errorSummary : errors._generic);

      setErrors(
        extend(errors, {
          cardNumber: errors.number,
          expirationDate: errors.expiration,
          securityCode: errors.cvc,
        })
      );

      setSubmitting(false);
    }
  },
});

export default compose(
  setDisplayName('paymentCardCreate'),
  errorBoundary,
  withStripeElements,
  connect(makeMapStateToProps, mapDispatchToProps),
  enhanceWithFormik,
  withStandardPageAndLoading('Add a credit card')
)(Renderer);
